import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { Static } from "../../interfaces/Static";
import {
  getDropdownsByID,
  getMonthNumber,
  isSouthAfricanID,
} from "../../utils/Statics";
import { StaticTypes } from "../../enums/StaticTypes";
import { RegularDriverType } from "../../interfaces/PolicyHolder";
import { useRecoilState, useRecoilValue } from "recoil";
import { policyHolderState, regularDriverState } from "../../state/QuoteState";
import Heading from "../../components/Heading";
import FormHeading from "../../components/FormHeading";
import CustomSelect from "../../components/CustomSelect";
import CustomInput from "../../components/CustomInput";
import ErrorText from "../../components/ErrorText";
import CustomDivider from "../../components/CustomDivider";
import DateSelector from "../../components/DateSelector";
import {
  extractDetailsFromIdNumber,
  isEmpty,
  isIdNumberValid,
} from "../../utils/Common";
import { differenceInYears, isValid, parseISO } from "date-fns";
import CustomRadio from "../../components/CustomRadio";

const RegularDriver = ({
  setIsFormValid,
}: {
  setIsFormValid: (value: boolean) => void;
}) => {
  const { data: statics } = useQuery<Static[]>({ queryKey: ["statics"] });
  const [idNumberNotValid, setIdNumberNotValid] = useState(false);
  const [isRegularDriverOlderThan18, setIsRegularDriverOlderThan18] =
    useState<boolean>(false);
  const [isRetiredAndYoungerThan50, setIsRetiredAndYoungerThan50] =
    useState<boolean>(false);
  const [phoneNumberStartsWithZero, setPhoneNumberStartsWithZero] =
    useState<boolean>(true);
  const [phoneNumberLessThan10Digits, setPhoneNumberLessThan10Digits] =
    useState<boolean>(false);
  const [emailNotValid, setEmailNotValid] = useState<boolean | null>(null);
  const [
    {
      day,
      email,
      employmentStatus,
      gender,
      idNumber,
      idType,
      maritalStatus,
      mobilePhoneNumber,
      month,
      name,
      surname,
      title,
      year,
      policyHolderIsDriver,
      relationshipToPolicyHolder,
    },
    setRegularDriver,
  ] = useRecoilState(regularDriverState);

  const policyHolder = useRecoilValue(policyHolderState);

  const {
    relationshipsToPolicyHolder,
    idTypes,
    titles,
    genders,
    maritalStatuses,
    employmentStatuses,
  } = useMemo(() => {
    return {
      relationshipsToPolicyHolder: getDropdownsByID(
        StaticTypes.RELATIONSHIP_TYPES,
        statics || [],
        true
      ),
      idTypes: getDropdownsByID(StaticTypes.IDTYPES, statics || [])?.filter(
        (s) => s?.code !== "3",
        true
      ),
      titles: getDropdownsByID(StaticTypes.TITLES, statics || [], true),
      genders: getDropdownsByID(StaticTypes.GENDERS, statics || []),
      maritalStatuses: getDropdownsByID(
        StaticTypes.MARITAL_STATUSES,
        statics || [],
        true
      ),
      employmentStatuses: getDropdownsByID(
        StaticTypes.EMPLOYMENT_STATUSES,
        statics || [],
        true
      ),
    };
  }, [statics]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (policyHolderIsDriver === true) {
      setRegularDriver((prev) => ({
        ...prev,
        title: policyHolder.title,
        name: policyHolder.name,
        surname: policyHolder.surname,
        day: policyHolder.day,
        month: policyHolder.month,
        year: policyHolder.year,
        idNumber: policyHolder.idNumber,
        idType: policyHolder.idType,
        gender: policyHolder.gender,
        mobilePhoneNumber: policyHolder.mobilePhoneNumber,
        employmentStatus: policyHolder.employmentStatus,
        maritalStatus: policyHolder.maritalStatus,
        email: policyHolder.email,
      }));
    }
    if (policyHolderIsDriver === false) {
      setRegularDriver((prev) => ({
        ...prev,
        title: "",
        name: "",
        surname: "",
        day: "",
        month: "",
        year: "",
        idNumber: "",
        idType: "",
        gender: "",
        mobilePhoneNumber: "",
        employmentStatus: "",
        maritalStatus: "",
        email: "",
        relationshipToPolicyHolder: "",
      }));
    }
  }, [policyHolderIsDriver]);

  useEffect(() => {
    if (isSouthAfricanID(idType)) {
      const payload = extractDetailsFromIdNumber(idNumber);

      setRegularDriver((prev) => ({ ...prev, ...payload }));
      if (idNumber.length === 13) {
        setIdNumberNotValid(!isIdNumberValid(idNumber));
      } else {
        setIdNumberNotValid(false);
      }
    } else {
      setIdNumberNotValid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idType, idNumber]);

  useEffect(() => {
    const dateString = `${year}-${getMonthNumber(month)}-${day}`;
    if (!isEmpty(day) && !isEmpty(month) && !isEmpty(year)) {
      if (isValid(parseISO(dateString))) {
        const age = differenceInYears(new Date(), new Date(dateString));
        setIsRegularDriverOlderThan18(age >= 18);
      } else {
        setIsRegularDriverOlderThan18(false);
      }
    } else {
      setIsRegularDriverOlderThan18(true);
    }
  }, [day, month, year]);

  useEffect(() => {
    const dateString = `${year}-${getMonthNumber(month)}-${day}`;
    if (employmentStatus === "RETIRED" && isValid(parseISO(dateString))) {
      const age = differenceInYears(new Date(), new Date(dateString));
      setIsRetiredAndYoungerThan50(age < 50);
    } else {
      setIsRetiredAndYoungerThan50(false);
    }
  }, [day, month, year, employmentStatus]);

  useEffect(() => {
    if (
      mobilePhoneNumber.length === 10 &&
      mobilePhoneNumber.slice(0, 1) !== "0"
    ) {
      setPhoneNumberStartsWithZero(false);
    } else {
      setPhoneNumberStartsWithZero(true);
    }

    if (mobilePhoneNumber.length > 1 && mobilePhoneNumber.length < 10) {
      setPhoneNumberLessThan10Digits(true);
    } else {
      setPhoneNumberLessThan10Digits(false);
    }
  }, [mobilePhoneNumber]);

  useEffect(() => {
    if (email.length > 3) {
      const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      setEmailNotValid(!regex.test(email)); // E-Mail Validierung
    } else {
      setEmailNotValid(false);
    }
  }, [email]);

  useEffect(() => {
    if (policyHolderIsDriver) {
      setIsFormValid(true);
    } else {
      setIsFormValid(
        idType !== "" &&
          (isSouthAfricanID(idType)
            ? idNumber.length === 13
            : idNumber !== "") &&
          title !== "" &&
          name !== "" &&
          surname !== "" &&
          day !== "" &&
          month !== "" &&
          year !== "" &&
          gender !== "" &&
          maritalStatus !== "" &&
          employmentStatus !== "" &&
          mobilePhoneNumber !== "" &&
          email !== "" &&
          isRegularDriverOlderThan18 &&
          !idNumberNotValid &&
          !isRetiredAndYoungerThan50 &&
          phoneNumberStartsWithZero &&
          !phoneNumberLessThan10Digits
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    day,
    email,
    employmentStatus,
    gender,
    idNumber,
    idType,
    maritalStatus,
    mobilePhoneNumber,
    month,
    name,
    surname,
    title,
    year,
    isRegularDriverOlderThan18,
    idNumberNotValid,
    isRetiredAndYoungerThan50,
    phoneNumberLessThan10Digits,
    policyHolderIsDriver,
  ]);

  const handleInputChange = (
    value: string | boolean,
    field: keyof RegularDriverType
  ) => {
    setRegularDriver((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <Heading tooltip="The individual who uses the insured vehicle the most is known as the regular driver. The policyholder may or may not be the regular driver.">
        Regular Driver
      </Heading>
      <CustomRadio
        selected={policyHolderIsDriver}
        onSelect={(value: boolean) => {
          handleInputChange(value, "policyHolderIsDriver");
        }}
        name="policyHolderIsRegularDriver"
        required
      >
        Is the regular driver also the policy holder?
      </CustomRadio>
      {policyHolderIsDriver === false && (
        <>
          <CustomSelect
            items={relationshipsToPolicyHolder}
            value={relationshipToPolicyHolder}
            placeholder="Relationship to policy holder"
            required
            onSelect={(value: string) => {
              handleInputChange(value, "relationshipToPolicyHolder");
            }}
          />
          <FormHeading>Regular driver ID</FormHeading>
          <CustomSelect
            items={idTypes}
            value={idType}
            placeholder="Id Type"
            required
            onSelect={(value: string) => {
              handleInputChange(value, "idType");
            }}
          />
          <CustomInput
            placeholder="ID / Passport Number"
            value={idNumber}
            max={isSouthAfricanID(idType) ? 13 : 40}
            onChange={(value) => {
              handleInputChange(value, "idNumber");
            }}
            required
          />
          {idNumberNotValid && <ErrorText>ID Number not valid!</ErrorText>}

          <CustomDivider />

          <FormHeading>Regular driver name</FormHeading>
          <CustomSelect
            items={titles}
            value={title}
            placeholder="Title"
            onSelect={(value: string) => {
              handleInputChange(value, "title");
            }}
            required
          />
          <CustomInput
            placeholder="Name"
            value={name}
            onChange={(value) => {
              handleInputChange(value, "name");
            }}
            max={30}
            required
          />
          <CustomInput
            placeholder="Surname"
            value={surname}
            onChange={(value) => {
              handleInputChange(value, "surname");
            }}
            max={30}
            required
          />

          <CustomDivider />

          <FormHeading>Birthdate</FormHeading>
          <DateSelector
            day={day}
            month={month}
            year={year}
            onDaySelect={(value) => {
              handleInputChange(value, "day");
            }}
            onMonthSelect={(value) => {
              handleInputChange(value, "month");
            }}
            onYearSelect={(value) => {
              handleInputChange(value, "year");
            }}
            disabled={isSouthAfricanID(idType)}
            min18Years={true}
            required
          />

          {isRegularDriverOlderThan18 === false && (
            <ErrorText>
              Regular driver is younger than 18 Years! The holder has to be
              older than 18 years!
            </ErrorText>
          )}
          {isRetiredAndYoungerThan50 === true && (
            <ErrorText>
              Regular driver is younger than 50 years and is retired!
            </ErrorText>
          )}

          <CustomDivider />

          <FormHeading>Personal information</FormHeading>
          <CustomSelect
            items={genders}
            value={gender}
            placeholder="Gender"
            onSelect={(value: string) => {
              handleInputChange(value, "gender");
            }}
            required
          />
          <CustomSelect
            items={maritalStatuses}
            value={maritalStatus}
            placeholder="Marital status"
            onSelect={(value: string) => {
              handleInputChange(value, "maritalStatus");
            }}
            required
          />
          <CustomSelect
            items={employmentStatuses}
            value={employmentStatus}
            placeholder="Employment status"
            onSelect={(value: string) => {
              handleInputChange(value, "employmentStatus");
            }}
            required
          />

          <CustomDivider />

          <FormHeading>Contact data</FormHeading>
          <CustomInput
            placeholder="Mobile phone number"
            value={mobilePhoneNumber}
            onChange={(value) => {
              handleInputChange(value, "mobilePhoneNumber");
            }}
            max={10}
            required
          />
          {phoneNumberLessThan10Digits && (
            <ErrorText>
              The mobile phone number must be 10 numbers long!
            </ErrorText>
          )}
          {phoneNumberStartsWithZero !== true && (
            <ErrorText>
              The first number of the mobile phone number must be 0!
            </ErrorText>
          )}
          <CustomInput
            placeholder="Email address"
            value={email}
            onChange={(value) => {
              handleInputChange(value, "email");
            }}
            type="email"
            max={40}
            required
          />
          {emailNotValid === true && (
            <ErrorText>Invalid email address!</ErrorText>
          )}
        </>
      )}
    </>
  );
};

export default RegularDriver;
